import { Badge } from "../../bausteine/badge";
import { zaehlerText, type GlockeArt } from "./glocke-logik";

/**
 * Zähler am Symbol: der Baustein `Badge` im Ton „fehler“ (helle Pille nach M-001, Rot nur als helle Pille), kein Roh-Rot in
 * dieser Datei, kein 10-px-Sonderwert (Behebung K3, AW-085 (3)).
 */
export function GlockeZaehler({ ungelesen }: { ungelesen: number }) {
  const text = zaehlerText(ungelesen);
  if (!text) return null;
  return (
    <span className="flex absolute top-0 inset-e-0 z-10 -mt-1.5 -me-1.5 tabular-nums" aria-hidden="true">
      <Badge ton="fehler">{text}</Badge>
    </span>
  );
}

/** Lucide-Symbole des Benachrichtigungsmenüs (R-F: Pfade der Lucide-Symbole, `currentColor`, nie Emoji). */
function Svg({ klasse = "shrink-0 size-4", children }: { klasse?: string; children: React.ReactNode }) {
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-dropdown-menus/notifications-menu (Symbole Bell, Settings, CheckCheck; Herkunftssymbole der Einträge als Lucide) */}
      <svg className={klasse} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
      </svg>
    </>
  );
}

/** Glocke (Lucide `Bell`). */
export function GlockeSymbol({ klasse }: { klasse?: string }) {
  return (
    <Svg klasse={klasse}>
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </Svg>
  );
}

/** Zahnrad (Lucide `Settings`). */
export function ZahnradSymbol() {
  return (
    <Svg>
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </Svg>
  );
}

/** Doppelhaken (Lucide `CheckCheck`). */
export function AlleGelesenSymbol() {
  return (
    <Svg>
      <path d="M18 6 7 17l-5-5" />
      <path d="m22 10-7.5 7.5L13 16" />
    </Svg>
  );
}

const ART_SYMBOL: Record<GlockeArt, () => React.ReactNode> = {
  // Lucide `UserRound`
  konto: () => (
    <>
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </>
  ),
  // Lucide `Utensils`
  kantine: () => (
    <>
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
    </>
  ),
  // Lucide `Calendar`
  besprechung: () => (
    <>
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </>
  ),
  // Lucide `Users`
  vertretung: () => (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  // Lucide `FolderKanban`
  projekt: () => (
    <>
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
      <path d="M8 10v4" />
      <path d="M12 10v2" />
      <path d="M16 10v6" />
    </>
  ),
  // Lucide `MessageSquare`
  chat: () => <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />,
  // Lucide `Info`
  system: () => (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </>
  ),
};

/** Symbol der Herkunftsart im Kreis eines Eintrags. */
export function ArtSymbol({ art }: { art: GlockeArt }) {
  return <Svg>{(ART_SYMBOL[art] ?? ART_SYMBOL.system)()}</Svg>;
}
